import React from 'react';
import { Text, TouchableOpacity, View } from 'react-native';

import { adminTableStyles as styles } from './adminTableStyles';

export interface TablePagerPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface TablePagerProps {
  label: string;
  page: number;
  pageSize: number;
  pageSizes: readonly number[];
  pagination: TablePagerPagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  itemNoun?: string;
}

const nextPageSize = (
  pageSizes: readonly number[],
  pageSize: number,
): number => {
  const index = pageSizes.indexOf(pageSize);
  if (index < 0) {
    return pageSizes[0];
  }
  return pageSizes[(index + 1) % pageSizes.length];
};

export const TablePager: React.FC<TablePagerProps> = ({
  label,
  page,
  pageSize,
  pageSizes,
  pagination,
  onPageChange,
  onPageSizeChange,
  itemNoun,
}) => {
  const testId = `${label.toLowerCase()}-pager`;
  const start = pagination.totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, pagination.totalCount);
  const totalPages = Math.max(pagination.totalPages, 1);
  const isFirstPage = page <= 1;
  const isLastPage = page >= pagination.totalPages;
  const rangeSuffix = itemNoun ? ` ${itemNoun}` : '';

  return (
    <View style={styles.pager} testID={testId}>
      <View
        style={styles.pagerSummary}
        testID={`${label.toLowerCase()}-pager-summary`}
      >
        <Text
          style={styles.pagerRange}
          accessibilityLiveRegion="polite"
          testID={`${label.toLowerCase()}-range`}
        >
          Showing {start}–{end} of {pagination.totalCount}
          {rangeSuffix}
        </Text>

        <View style={styles.pagerSize}>
          <Text style={styles.pagerLabel}>Rows per page:</Text>
          <TouchableOpacity
            style={styles.pageSizeButton}
            onPress={() => onPageSizeChange(nextPageSize(pageSizes, pageSize))}
            accessibilityRole="button"
            accessibilityLabel={`Rows per page ${pageSize}. Tap to change`}
            testID={`${label.toLowerCase()}-page-size`}
          >
            <Text style={styles.pageSizeText}>{pageSize}</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View
        style={styles.pagerControls}
        testID={`${label.toLowerCase()}-pager-controls`}
      >
        <TouchableOpacity
          style={[styles.pageButton, isFirstPage && styles.pageButtonDisabled]}
          onPress={() => onPageChange(page - 1)}
          disabled={isFirstPage}
          accessibilityRole="button"
          accessibilityLabel={`Previous ${label.toLowerCase()} page`}
        >
          <Text
            style={[
              styles.pageButtonText,
              isFirstPage && styles.pageButtonTextDisabled,
            ]}
          >
            Previous
          </Text>
        </TouchableOpacity>

        <Text
          style={styles.pageInfo}
          testID={`${label.toLowerCase()}-page-info`}
        >
          Page {page} of {totalPages}
        </Text>

        <TouchableOpacity
          style={[styles.pageButton, isLastPage && styles.pageButtonDisabled]}
          onPress={() => onPageChange(page + 1)}
          disabled={isLastPage}
          accessibilityRole="button"
          accessibilityLabel={`Next ${label.toLowerCase()} page`}
        >
          <Text
            style={[
              styles.pageButtonText,
              isLastPage && styles.pageButtonTextDisabled,
            ]}
          >
            Next
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};
